import {
  type ParticipantBehaviour,
  ParticipantBehaviourStatus,
  type UpdateParticipantBehaviourRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z, zodNonEmptyString } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateParticipantBehaviourMutation } from '../../../graphql/hooks/useUpdateParticipantBehaviourMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantBehaviourForm } from './ParticipantBehaviourForm';

type FormValues = UpdateParticipantBehaviourRequest;

export const methodsSchema = z.object({
  abc: z.object({
    id: z.string(),
    enabled: z.boolean(),
    templates: z.object({
      antecedent: z.array(zodNonEmptyString()),
      behaviour: z.array(zodNonEmptyString()),
      consequence: z.array(zodNonEmptyString()),
    }),
  }),
  duration: z.object({
    id: z.string(),
    enabled: z.boolean(),
  }),
  frequency: z.object({
    id: z.string(),
    enabled: z.boolean(),
  }),
  episodicSeverity: z
    .object({
      id: z.string(),
      enabled: z.boolean(),
      scales: z.array(
        z.object({
          id: z.string(),
          description: zodNonEmptyString(),
        }),
      ),
    })
    .check((ctx) => {
      const { enabled, scales } = ctx.value;

      if (!enabled) {
        return;
      }

      if (scales.length < 2) {
        ctx.issues.push({
          code: 'custom',
          message: 'Please add at least 2 episodic severity scales',
          path: ['scales'],
          input: ctx.value.scales,
        });
      }

      if (scales.length > 10) {
        ctx.issues.push({
          code: 'custom',
          message: 'You have reached the limit of 10 episodic severity scales',
          path: ['scales'],
          input: ctx.value.scales,
        });
      }
    }),
});

const schema: z.ZodSchema<FormValues> = z.object({
  participantId: z.string(),
  name: z.string().nonempty('Please enter a name'),
  status: z.enum(ParticipantBehaviourStatus),
  description: z.string().nonempty('Please enter a description'),
  example: z.string().nonempty('Please enter an example'),
  nonExample: z.string().nonempty('Please enter a non-example'),
  methods: methodsSchema,
});

interface Props {
  participantId: string;
  participantBehaviour: ParticipantBehaviour;
}

export const ParticipantBehaviourEditModal = createModal<Props>((props) => {
  const { modal, participantId, participantBehaviour } = props;
  const { visible, hide, remove } = modal;
  const { organization, successToast, errorToast } = useAppContext();

  const defaultValues = {
    participantId,
    ...participantBehaviour,
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateParticipantBehaviourMutation();

  const onSubmit = (values: FormValues) => {
    mutation
      .mutateAsync({
        organizationId: organization.id,
        participantBehaviourId: participantBehaviour.id,
        request: values,
      })
      .then(() => {
        hide();
        successToast('Participant behaviour updated successfully');
      })
      .catch((err) => {
        errorToast(`Error updating participant behaviour: ${err.message}`);
      });
  };

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='xl'>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Update a participant behaviour'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantBehaviourForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
